/* eslint-disable no-unused-vars */
import React, { useMemo, useState } from 'react';
import {
  Button,
  Col,
  Input,
  InputNumber,
  Modal,
  Row,
  Select,
  Table,
  Typography,
  Upload,
  notification,
} from 'antd';
import { UploadOutlined } from '@ant-design/icons';
import legacyOtaServices from '../../../../services/legacy-ota.services';

const { Text } = Typography;

const environmentOptions = [
  { value: 'dev', label: 'dev' },
  // { value: 'uat', label: 'uat' },
  { value: 'prod', label: 'prod' },
];

const createdByFromUser = (user) =>
  user?.name || user?.user_name || user?.username || user?.user_id || '';

const OtaFormModal = ({ open, user, onClose, onSuccess }) => {
  const [environment, setEnvironment] = useState('dev');
  const [version, setVersion] = useState(null);
  const [targetAppVersion, setTargetAppVersion] = useState('');
  const [minBuildNumber, setMinBuildNumber] = useState(null);
  const [maxBuildNumber, setMaxBuildNumber] = useState(null);
  const [description, setDescription] = useState('');
  const [androidFile, setAndroidFile] = useState(null);
  const [iosFile, setIosFile] = useState(null);

  const { data: existingVersions } = legacyOtaServices.useQueryGetOtaVersions(
    { environment, active: undefined },
    () => {},
  );

  const { mutate: createOtaVersion, isLoading: isCreating } =
    legacyOtaServices.useMutationCreateOtaVersion(
      () => {
        notification.success({
          message: `สร้าง version ${version} สำเร็จ`,
          description:
            'ยังไม่เปิดใช้งาน — กดสวิตช์ ON ในตารางเพื่อเริ่ม rollout',
        });
        resetForm();
        onSuccess();
      },
      (error) => {
        notification.error({
          message: 'อัปโหลดไม่สำเร็จ',
          description: error?.message || error,
        });
      },
    );

  const resetForm = () => {
    setVersion(null);
    setTargetAppVersion('');
    setMinBuildNumber(null);
    setMaxBuildNumber(null);
    setDescription('');
    setAndroidFile(null);
    setIosFile(null);
  };

  const versions = existingVersions?.data || [];

  // hint แสดง version mapping ปัจจุบันของ env นี้ — เห็นว่า user กลุ่มไหนจะได้อะไร
  const activeMappings = useMemo(
    () => versions.filter((v) => v.active),
    [versions],
  );

  const activeVersionsOfEnv = versions
    .filter((v) => v.active)
    .map((v) => `v${v.version}`)
    .join(', ');

  const validateAndSubmit = () => {
    if (!version || version <= 0) {
      notification.warning({ message: 'กรอก version (จำนวนเต็มบวก)' });
      return;
    }
    if (!targetAppVersion) {
      notification.warning({ message: 'กรอก targetAppVersion เช่น 2.3.8' });
      return;
    }
    if (minBuildNumber === null || minBuildNumber < 0) {
      notification.warning({ message: 'กรอก minBuildNumber' });
      return;
    }
    if (maxBuildNumber !== null && maxBuildNumber < minBuildNumber) {
      notification.warning({
        message: 'maxBuildNumber ต้องมากกว่าหรือเท่ากับ minBuildNumber',
      });
      return;
    }
    if (!androidFile && !iosFile) {
      notification.warning({
        message: 'แนบ zip อย่างน้อย 1 ฝั่ง (Android หรือ iOS)',
      });
      return;
    }

    // heuristic ชื่อไฟล์ — เตือนถ้า zip ผิด platform
    const warnings = [];
    if (
      androidFile &&
      !androidFile.name.includes('index.android.bundle') &&
      !androidFile.name.includes('android')
    ) {
      warnings.push(
        `ไฟล์ android "${androidFile.name}" ไม่มีชื่อตรงกับ pattern index.android.bundle.zip — ตรวจสอบว่าอัปถูกฝั่ง`,
      );
    }
    if (
      iosFile &&
      !iosFile.name.includes('main.jsbundle') &&
      !iosFile.name.includes('ios')
    ) {
      warnings.push(
        `ไฟล์ ios "${iosFile.name}" ไม่มีชื่อตรงกับ pattern main.jsbundle.zip — ตรวจสอบว่าอัปถูกฝั่ง`,
      );
    }

    const submit = () => {
      const formData = new FormData();
      formData.append('version', String(version));
      formData.append('environment', environment);
      formData.append('targetAppVersion', targetAppVersion);
      formData.append('minBuildNumber', String(minBuildNumber));
      if (maxBuildNumber !== null) {
        formData.append('maxBuildNumber', String(maxBuildNumber));
      }
      if (description) formData.append('description', description);
      const createdBy = createdByFromUser(user);
      if (createdBy) formData.append('createdBy', createdBy);
      if (androidFile) formData.append('androidBundle', androidFile);
      if (iosFile) formData.append('iosBundle', iosFile);
      createOtaVersion(formData);
    };

    if (warnings.length > 0) {
      Modal.confirm({
        title: 'ชื่อไฟล์ดูไม่ตรง platform — ยังจะอัปต่อไหม?',
        content: warnings.join('\n'),
        okText: 'อัปต่อ',
        cancelText: 'ยกเลิก',
        zIndex: 1080,
        onOk: submit,
      });
      return;
    }

    submit();
  };

  return (
    <Modal
      visible={open}
      title="อัปโหลด OTA version ใหม่"
      okText="อัปโหลด"
      okButtonProps={{ loading: isCreating }}
      cancelText="ยกเลิก"
      width={720}
      zIndex={1080}
      onOk={validateAndSubmit}
      onCancel={() => {
        resetForm();
        onClose();
      }}
    >
      <Row className="mb-2 align-items-center">
        <Col span={6}>
          <Text>
            Environment <span style={{ color: 'red' }}>*</span>
          </Text>
        </Col>
        <Col span={18}>
          <Select
            className="w-100"
            value={environment}
            onChange={setEnvironment}
          >
            {environmentOptions.map((opt) => (
              <Select.Option key={opt.value} value={opt.value}>
                {opt.label}
              </Select.Option>
            ))}
          </Select>
          {activeVersionsOfEnv && (
            <Text type="secondary">
              version ที่เปิดอยู่ใน {environment}: {activeVersionsOfEnv}
            </Text>
          )}
        </Col>
      </Row>

      <Row className="mb-2 align-items-center">
        <Col span={6}>
          <Text>
            Version (int) <span style={{ color: 'red' }}>*</span>
          </Text>
        </Col>
        <Col span={18}>
          <InputNumber
            className="w-100"
            min={1}
            precision={0}
            value={version}
            onChange={setVersion}
            placeholder="ต่อจาก build number ปัจจุบัน เช่น 833 → อัป 834"
          />
        </Col>
      </Row>

      <Row className="mb-2 align-items-center">
        <Col span={6}>
          <Text>
            Target App Version <span style={{ color: 'red' }}>*</span>
          </Text>
        </Col>
        <Col span={18}>
          <Input
            value={targetAppVersion}
            onChange={(e) => setTargetAppVersion(e.target.value)}
            placeholder="binary versionName ที่ bundle build จาก เช่น 2.3.8"
          />
        </Col>
      </Row>

      <Row className="mb-2 align-items-center">
        <Col span={6}>
          <Text>
            Min Build Number <span style={{ color: 'red' }}>*</span>
          </Text>
        </Col>
        <Col span={8}>
          <InputNumber
            className="w-100"
            min={0}
            precision={0}
            value={minBuildNumber}
            onChange={setMinBuildNumber}
            placeholder="เช่น 833"
          />
        </Col>
        <Col span={4} className="text-center">
          <Text>Max (ไม่ใส่ = ไม่จำกัด)</Text>
        </Col>
        <Col span={6}>
          <InputNumber
            className="w-100"
            min={0}
            precision={0}
            value={maxBuildNumber}
            onChange={setMaxBuildNumber}
            placeholder="เช่น 839"
          />
        </Col>
      </Row>

      <Row className="mb-2 align-items-start">
        <Col span={6}>
          <Text>รายละเอียด</Text>
        </Col>
        <Col span={18}>
          <Input.TextArea
            rows={2}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="release note"
          />
        </Col>
      </Row>

      <Row className="mb-2 align-items-center">
        <Col span={6}>
          <Text>
            Android zip <span style={{ color: 'red' }}>*</span>
          </Text>
        </Col>
        <Col span={18}>
          <Upload
            accept=".zip"
            maxCount={1}
            fileList={androidFile ? [androidFile] : []}
            beforeUpload={(file) => {
              setAndroidFile(file);
              return false;
            }}
            onRemove={() => setAndroidFile(null)}
          >
            <Button icon={<UploadOutlined />}>
              index.android.bundle.zip (export-android)
            </Button>
          </Upload>
        </Col>
      </Row>

      <Row className="mb-2 align-items-center">
        <Col span={6}>
          <Text>
            iOS zip <span style={{ color: 'red' }}>*</span>
          </Text>
        </Col>
        <Col span={18}>
          <Upload
            accept=".zip"
            maxCount={1}
            fileList={iosFile ? [iosFile] : []}
            beforeUpload={(file) => {
              setIosFile(file);
              return false;
            }}
            onRemove={() => setIosFile(null)}
          >
            <Button icon={<UploadOutlined />}>
              main.jsbundle.zip (export-ios)
            </Button>
          </Upload>
        </Col>
      </Row>

      <Text type="secondary">
        อัปอย่างน้อย 1 ฝั่ง — URL เกิดอัตโนมัติหลังอัปโหลด (ไม่มีการกรอก URL
        เอง) และต้องเปิดสวิตช์ในตารางก่อน app จะเห็น version นี้
      </Text>

      {activeMappings.length > 0 && (
        <div className="mt-3">
          <Text strong>version mapping ปัจจุบัน ({environment}):</Text>
          <Table
            size="small"
            bordered
            rowKey="id"
            pagination={false}
            dataSource={activeMappings}
            columns={[
              { title: 'Version', dataIndex: 'version', width: 90 },
              {
                title: 'Target App',
                dataIndex: 'targetAppVersion',
                width: 110,
              },
              {
                title: 'Build Range',
                width: 120,
                render: (_, record) =>
                  `${record.minBuildNumber}${
                    record.maxBuildNumber ? ` - ${record.maxBuildNumber}` : '+'
                  }`,
              },
              {
                title: 'แพ็กเกจ',
                render: (_, record) =>
                  `${record.androidBundleUrl ? 'Android' : ''}${
                    record.androidBundleUrl && record.iosBundleUrl ? ' + ' : ''
                  }${record.iosBundleUrl ? 'iOS' : ''}`,
              },
            ]}
          />
        </div>
      )}
    </Modal>
  );
};

export default OtaFormModal;
